import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function ExpensesReport() {
  const [expenses, setExpenses] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [driverNameByEmail, setDriverNameByEmail] = useState({});
  const company = useCompanyInfo();

  const CATEGORIES = ['Fuel', 'Toll', 'Scale', 'Maintenance', 'Tire', 'Other'];

  useEffect(() => {
    client.entities.Expense.list('-date', 500).then(e => { setExpenses(e); setLoading(false); });
    client.entities.DriverProfile.list('-updated_date', 500).then(profiles => {
      const map = {};
      for (const p of profiles) if (p.user_email) map[p.user_email.toLowerCase()] = p.full_name;
      setDriverNameByEmail(map);
    });
  }, []);

  const driverLabel = (email) => (email && driverNameByEmail[email.toLowerCase()]) || email || '';

  const filtered = expenses.filter(e =>
    inRange(e.date, start, end) &&
    (!categoryFilter || e.category === categoryFilter)
  );

  const byCategory = CATEGORIES.reduce((acc, cat) => {
    const rows = filtered.filter(e => e.category === cat);
    if (rows.length > 0) acc[cat] = rows.reduce((s, e) => s + (e.amount || 0), 0);
    return acc;
  }, {});

  const total = filtered.reduce((s, e) => s + (e.amount || 0), 0);

  const csvRows = filtered.map(e => ({
    Date: e.date || '',
    Driver: driverLabel(e.driver_email),
    Category: e.category || '',
    Description: e.note || '',
    Amount: (e.amount || 0).toFixed(2),
  }));

  const handlePrint = () => {
    const cols = ['Date', 'Driver', 'Category', 'Description', 'Amount'];
    const dataRows = filtered.map(e => [
      e.date || '', driverLabel(e.driver_email), e.category || '', e.note || '—',
      { value: `$${(e.amount || 0).toFixed(2)}`, class: 'negative' },
    ]);
    const totalsRow = ['TOTAL', '', '', '', { value: `$${total.toFixed(2)}`, class: 'gold' }];
    const catEntries = Object.entries(byCategory);
    const cards = [
      { label: 'Total Expenses', value: `$${total.toFixed(2)}`, color: 'negative' },
      { label: 'Records', value: filtered.length, color: '' },
      ...catEntries.slice(0, 2).map(([cat, amt]) => ({ label: cat, value: `$${amt.toFixed(2)}`, color: 'negative' })),
    ];
    const html = generatePrintHTML('Expenses Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'expenses.csv')} exportDisabled={loading || filtered.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground">Category:</label>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
          <option value="" className="bg-background">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c} className="bg-background">{c}</option>)}
        </select>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Date', 'Driver', 'Category', 'Description', 'Amount'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(e => (
              <tr key={e.id} className="hover:bg-white/3">
                <td className="px-3 py-2.5 text-muted-foreground">{e.date}</td>
                <td className="px-3 py-2.5 text-foreground">{driverLabel(e.driver_email)}</td>
                <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full bg-muted/60 text-foreground">{e.category}</span></td>
                <td className="px-3 py-2.5 text-muted-foreground">{e.note || '—'}</td>
                <td className="px-3 py-2.5 font-semibold text-foreground">${(e.amount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t border-border bg-white/5">
                <td colSpan={4} className="px-3 py-2.5 font-bold text-foreground">TOTAL</td>
                <td className="px-3 py-2.5 font-bold text-foreground">${total.toFixed(2)}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {filtered.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No expenses found.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
      {Object.keys(byCategory).length > 0 && (
        <div className="glass-card p-4">
          <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-widest">By Category</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(byCategory).map(([cat, amt]) => (
              <div key={cat} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/40 border border-border">
                <span className="text-xs text-foreground">{cat}</span>
                <span className="text-xs font-bold text-primary">${amt.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}